"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PolkadotProvider = void 0;
const api_1 = require("@polkadot/api");
const extension_dapp_1 = require("@polkadot/extension-dapp");
const util_crypto_1 = require("@polkadot/util-crypto");
const bignumber_js_1 = __importDefault(require("bignumber.js"));
class PolkadotProvider {
    constructor(config) {
        this.config = config;
        this.networkType = config.networkType;
    }
    static isSupported() {
        return extension_dapp_1.isWeb3Injected;
    }
    static web3FromAddress(address) {
        return extension_dapp_1.web3FromAddress(address);
    }
    getRawApi() {
        if (!this.api)
            throw new Error(`Polkadot API is not initialized`);
        return this.api;
    }
    isConnected() {
        return !!this.api;
    }
    connect() {
        return __awaiter(this, void 0, void 0, function* () {
            yield extension_dapp_1.web3Enable('StakeFi');
            const accounts = yield this.getAccounts();
            console.log(`Found next polkadot accounts: ${accounts}`);
            if (!accounts.length) {
                throw new Error('There are no Polkadot accounts');
            }
            const wsProvider = new api_1.WsProvider(this.config.polkadotUrl);
            this.api = yield api_1.ApiPromise.create({ provider: wsProvider });
            yield this.getNetworkType();
        });
    }
    getNetworkType() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.networkType)
                return this.networkType;
            const chainMapping = {
                'POLKADOT': 'DOT',
                'KUSAMA': 'KSM',
                'WESTEND': 'WND',
                'ROCOCO': 'ROC',
            };
            const wsProvider = new api_1.WsProvider(this.config.polkadotUrl), api = yield api_1.ApiPromise.create({ provider: wsProvider });
            const chainName = yield api.rpc.system.chain();
            if (!chainMapping[chainName.toUpperCase()]) {
                throw new Error(`Not supported polkadot chain: ${chainName}`);
            }
            this.networkType = chainMapping[chainName.toUpperCase()];
            return this.networkType;
        });
    }
    _scaleUp(value) {
        return value.multipliedBy(Math.pow(10, this.calcDecimals()));
    }
    _scaleDown(value) {
        return value.dividedBy(Math.pow(10, this.calcDecimals()));
    }
    calcDecimals() {
        const decimals = {
            'DOT': 10,
            'KSM': 12,
            'WND': 12,
            'ROC': 12,
        };
        if (!this.networkType || !decimals[this.networkType]) {
            throw new Error(`Unable to calc decimals for network: ${this.networkType}`);
        }
        return decimals[this.networkType];
    }
    getAccountBalance(address) {
        return __awaiter(this, void 0, void 0, function* () {
            const { nonce, data } = yield this.getRawApi().query.system.account(address);
            return {
                reserved: this._scaleDown(new bignumber_js_1.default(data.reserved.toString(10))),
                miscFrozen: this._scaleDown(new bignumber_js_1.default(data.miscFrozen.toString(10))),
                free: this._scaleDown(new bignumber_js_1.default(data.free.toString(10))),
                feeFrozen: this._scaleDown(new bignumber_js_1.default(data.feeFrozen.toString(10))),
                nonce: new bignumber_js_1.default(nonce.toString()),
            };
        });
    }
    getMinSafeDepositVal() {
        const api = this.getRawApi();
        return this._scaleDown(new bignumber_js_1.default(api.consts.balances.existentialDeposit.toString()));
    }
    sendFundsWithExtrinsic(sender, recipient, amount) {
        return __awaiter(this, void 0, void 0, function* () {
            const { blockHash, transactionHash } = yield this.sendFundsTo(sender, recipient, amount), { extId } = yield this.getExtrinsicIndexByHash(transactionHash, blockHash);
            return { transactionHash, blockHash, extId: extId };
        });
    }
    getMaxPossibleSendAmount(sender, recipient, amount) {
        return __awaiter(this, void 0, void 0, function* () {
            const injector = yield extension_dapp_1.web3FromAddress(sender);
            if (!injector) {
                throw new Error(`Unable to connect to Polkadot extension using address: ${sender}`);
            }
            const api = this.getRawApi();
            const transferCall = api.tx.balances.transferKeepAlive(recipient, this._scaleUp(amount).toString(10));
            const signerOptions = {
                nonce: -1,
            };
            const paymentInfoResult = yield transferCall.paymentInfo(sender, signerOptions);
            console.log(`Payment info result: ${JSON.stringify(paymentInfoResult, null, 2)}`);
            const { free, miscFrozen } = yield this.getAccountBalance(sender);
            const minSafeDepositVal = this.getMinSafeDepositVal();
            const fee = this._scaleDown(new bignumber_js_1.default(paymentInfoResult.partialFee.toString()));
            const result = new bignumber_js_1.default(free).minus(minSafeDepositVal).minus(miscFrozen).minus(fee);
            console.log(`Max transferable balance: ${free} - ${minSafeDepositVal} - ${miscFrozen} - ${fee} = ${result}`);
            return result.isLessThanOrEqualTo(0) ? new bignumber_js_1.default(0) : result;
        });
    }
    sendFundsTo(sender, recipient, amount) {
        return __awaiter(this, void 0, void 0, function* () {
            const maxPossibleAmount = yield this.getMaxPossibleSendAmount(sender, recipient, amount);
            if (amount.isGreaterThan(maxPossibleAmount)) {
                throw new Error(`Not enough funds to pay transaction fee and keep account alive, max sending amount is ${maxPossibleAmount.toString(10)}`);
            }
            const injector = yield extension_dapp_1.web3FromAddress(sender);
            if (!injector || !injector.signer.signRaw)
                throw new Error(`Unable to connect to Polkadot extension using address: ${sender}`);
            const api = this.getRawApi();
            api.setSigner(injector.signer);
            console.log(`Creating transaction from ${sender} to ${recipient} amount ${amount}`);
            const transferCall = api.tx.balances.transferKeepAlive(recipient, this._scaleUp(amount).toString(10));
            const signerOptions = {
                nonce: -1,
            };
            let signedExtrinsic;
            try {
                signedExtrinsic = yield transferCall.signAsync(sender, signerOptions);
            }
            catch (e) {
                throw new Error(e);
            }
            /**
             *  @Note:
             *    This is an anti-pattern: https://stackoverflow.com/questions/43036229/is-it-an-anti-pattern-to-use-async-await-inside-of-a-new-promise-constructor
             *    A quick fix is a "solution" for reducing influence of possible errors with transaction sending and unsubscription
             */
            return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
                try {
                    const transactionHash = util_crypto_1.blake2AsHex(signedExtrinsic.toU8a(false), 256);
                    console.log(`Transaction hash (calculated): ${transactionHash}`);
                    const unsub = yield signedExtrinsic.send((result) => {
                        const { events = [], status, dispatchError } = result;
                        console.log('Transaction status:', status.type);
                        if (dispatchError) {
                            let errorMessage = dispatchError.toString();
                            if (dispatchError.isModule) {
                                const decoded = api.registry.findMetaError(dispatchError.asModule);
                                const { docs, name, section } = decoded;
                                errorMessage = `${section}.${name}: ${docs.join(' ')}`;
                            }
                            console.log(`Decoded module transaction error is: ${errorMessage}`);
                            unsub();
                            reject(new Error(errorMessage));
                        }
                        if (status.isInBlock) {
                            console.log('Included at block hash', status.asInBlock.toHex());
                            console.log('Events:');
                            events.forEach(({ event: { data, method, section }, phase }) => {
                                console.log('\t', phase.toString(), `: ${section}.${method}`, data.toString());
                            });
                        }
                        else if (status.isFinalized) {
                            console.log('Finalized block hash', status.asFinalized.toHex());
                            unsub();
                            resolve({
                                submittableResult: result,
                                blockHash: status.asFinalized.toHex(),
                                transactionHash: transactionHash,
                            });
                        }
                        else if (status.isDropped) {
                            unsub();
                            reject(new Error('Transaction has been dropped'));
                        }
                    });
                }
                catch (e) {
                    reject(new Error(e));
                }
            }));
        });
    }
    getExtrinsicIndexByHash(transactionHash, blockHash) {
        return __awaiter(this, void 0, void 0, function* () {
            const signedBlock = yield this.getRawApi().rpc.chain.getBlock(blockHash);
            const blockTxs = signedBlock.block.extrinsics.toArray();
            const indexAt = blockTxs.findIndex(signedExtrinsic => util_crypto_1.blake2AsHex(signedExtrinsic.toU8a(false), 256) === transactionHash);
            if (indexAt === -1)
                throw new Error(`Unable to find extrinsic by hash ${transactionHash} in block ${blockHash}`);
            return {
                extrinsic: blockTxs[indexAt],
                extId: `${signedBlock.block.header.number}-${indexAt}`,
                signedBlock: signedBlock,
                indexAt: indexAt,
            };
        });
    }
    getAccounts() {
        return __awaiter(this, void 0, void 0, function* () {
            const ss58Formats = {
                'DOT': 0,
                'KSM': 2,
                'WND': 42,
                'ROC': 42,
            };
            let ss58Format = 42;
            if (ss58Formats.hasOwnProperty(this.networkType)) {
                ss58Format = ss58Formats[this.networkType];
            }
            const accountsMeta = yield extension_dapp_1.web3Accounts({
                ss58Format: ss58Format,
            });
            return accountsMeta.map(meta => meta.address);
        });
    }
    signRawMessage(account, hexData) {
        return __awaiter(this, void 0, void 0, function* () {
            const injector = yield extension_dapp_1.web3FromAddress(account);
            if (!injector || !injector.signer.signRaw)
                throw new Error(`Unable to connect to Polkadot extension using address: ${account}`);
            const { signature } = yield injector.signer.signRaw({
                type: 'bytes',
                address: account,
                data: hexData
            });
            console.log(`Signed message: ${hexData} with signature: ${signature}`);
            return signature;
        });
    }
    static extractPublicKeyHexFromAddress(address) {
        return Buffer.from(util_crypto_1.decodeAddress(address)).toString('hex');
    }
}
exports.PolkadotProvider = PolkadotProvider;
//# sourceMappingURL=polkadot.js.map